/**
 * Ticket detail panel — the core support workflow.
 * Per docs/specs/support.md §12.2.
 *
 * Slides in from the right (440px via Shell). Renders the conversation thread,
 * customer context card, canned replies, and a reply composer with channel +
 * internal-note toggle.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-27-v2
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Bot, Mail, MessageCircle, Phone, Send, Smartphone, Sparkles, X } from 'lucide-react';
import { Chip } from '../../components/chip';
import { useCan } from '../../components/role-gate';
import { useToast } from '../../components/toast';
import {
    updateSupportTicket,
    useCannedReplies,
    useSupportTickets,
    useTicketMessages,
    useUser
} from '../../../lib/api';
import { apiPost } from '../../../lib/http';
import {
    getSupportTranscriptLabel,
    isInternalTicketMessage
} from '../../../lib/support-transcript';
import type { TicketChannel, TicketPriority, TicketStatus } from '../../../lib/types';

function ChannelIcon({ channel }: { channel: TicketChannel }) {
    if (channel === 'help_ai') return <Bot size={11} />;
    if (channel === 'email') return <Mail size={11} />;
    if (channel === 'chat') return <MessageCircle size={11} />;
    if (channel === 'sms') return <Smartphone size={11} />;
    return <Phone size={11} />;
}

export function TicketDetailPanel({
    ticketId,
    onClose
}: {
    ticketId: string;
    onClose: () => void;
}) {
    const qc = useQueryClient();
    const tickets = useSupportTickets();
    const messages = useTicketMessages(ticketId);
    const canned = useCannedReplies();
    const { toast } = useToast();

    const ticket = tickets.data?.find((t) => t.id === ticketId);
    const user = useUser(ticket?.user_id ?? null);
    const canCompose = useCan('support_ok');

    const [replyBody, setReplyBody] = useState('');
    const [internalNote, setInternalNote] = useState(false);
    const [channel, setChannel] = useState<TicketChannel>('help_ai');
    const [sending, setSending] = useState<
        'waiting_customer' | 'waiting_internal' | 'resolved' | null
    >(null);
    const [patchingTicket, setPatchingTicket] = useState(false);

    const channelOptions = useMemo<TicketChannel[]>(
        () =>
            ticket
                ? ticket.active_channels.length
                    ? ticket.active_channels
                    : ['email']
                : ['email'],
        [ticket]
    );
    const transcript = useMemo(
        () =>
            [...(messages.data ?? [])].sort((a, b) => {
                const left = new Date(a.created_at ?? a.ts).getTime();
                const right = new Date(b.created_at ?? b.ts).getTime();
                if (!Number.isFinite(left) || !Number.isFinite(right)) return 0;
                return left - right;
            }),
        [messages.data]
    );

    useEffect(() => {
        if (!channelOptions.includes(channel)) {
            setChannel(channelOptions[0] ?? 'help_ai');
        }
    }, [channel, channelOptions]);

    if (!ticket) {
        return (
            <div className="detail-panel-header">
                <div className="empty-state">Loading…</div>
            </div>
        );
    }

    const handleInsertCanned = (slug: string) => {
        if (!canCompose) return;
        const c = canned.data?.find((r) => r.slug === slug);
        if (c) setReplyBody((prev) => (prev ? `${prev}\n\n${c.body}` : c.body));
    };

    const handleSend = async (
        desiredStatus: 'waiting_customer' | 'waiting_internal' | 'resolved'
    ) => {
        if (!canCompose) return;
        const body = replyBody.trim();
        if (!body) return;

        setSending(desiredStatus);
        try {
            await apiPost(`/api/admin/support/tickets/${ticketId}/messages`, {
                body_text: body,
                channel,
                internal_note: internalNote,
                desired_status: desiredStatus
            });

            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admin', 'support', 'tickets'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'support', 'messages', ticketId] })
            ]);

            setReplyBody('');
            setInternalNote(false);
            const successMessage =
                desiredStatus === 'resolved'
                    ? 'Reply sent and ticket resolved'
                    : internalNote
                      ? 'Internal note saved'
                      : 'Reply sent';
            toast(successMessage, {
                kind: desiredStatus === 'resolved' ? 'success' : 'info'
            });
        } catch (error) {
            toast('Support action failed', {
                kind: 'danger',
                detail: error instanceof Error ? error.message : undefined
            });
        } finally {
            setSending(null);
        }
    };

    const handlePatchTicket = async (patch: {
        status?: TicketStatus;
        priority?: TicketPriority;
        assignee?: string | null;
    }) => {
        if (!canCompose || patchingTicket) return;
        setPatchingTicket(true);
        try {
            const updated = await updateSupportTicket(ticketId, patch);
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admin', 'support', 'tickets'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'support', 'messages', ticketId] })
            ]);
            toast('Ticket updated', {
                kind: 'info',
                detail: `#PD-${updated.number} · ${updated.status.replace('_', ' ')}`
            });
        } catch (error) {
            toast('Ticket update failed', {
                kind: 'danger',
                detail: error instanceof Error ? error.message : undefined
            });
        } finally {
            setPatchingTicket(false);
        }
    };

    const handleAiSuggest = () => {
        const suggestion =
            canned.data?.find((reply) =>
                ticket.subject.toLowerCase().includes('billing')
                    ? reply.slug.includes('billing')
                    : reply.slug.includes('follow')
            )?.body ??
            `Hi ${ticket.customer_name ?? 'there'},\n\nThanks for the note. We're checking this now and will follow up shortly.\n\nRoss`;
        setReplyBody(suggestion);
        toast('Inserted AI-suggested draft', { kind: 'info' });
    };

    return (
        <>
            <header className="detail-panel-header">
                <div style={{ minWidth: 0, flex: 1 }}>
                    <h2 className="name">#PD-{ticket.number}</h2>
                    <p className="email">{ticket.subject}</p>
                    <span className="uid">
                        {ticket.customer_name ?? ticket.customer_email}
                        {ticket.customer_name ? ` · ${ticket.customer_email}` : ''}
                    </span>
                </div>
                <button className="close" onClick={onClose} aria-label="Close">
                    <X size={16} />
                </button>
            </header>

            <div className="detail-panel-chips">
                <Chip
                    tone={
                        ticket.priority === 'urgent'
                            ? 'danger'
                            : ticket.priority === 'high'
                              ? 'warning'
                              : ticket.priority === 'normal'
                                ? 'info'
                                : 'muted'
                    }
                >
                    {ticket.priority}
                </Chip>
                <Chip
                    tone={
                        ticket.status === 'open'
                            ? 'info'
                            : ticket.status === 'in_progress'
                              ? 'purple'
                              : ticket.status === 'waiting_customer'
                                ? 'warning'
                                : ticket.status === 'resolved' || ticket.status === 'closed'
                                  ? 'success'
                                  : 'muted'
                    }
                    dot
                >
                    {ticket.status.replace('_', ' ')}
                </Chip>
                {ticket.active_channels.map((c) => (
                    <span
                        key={c}
                        className="chip"
                        data-tone="muted"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                        <ChannelIcon channel={c} />
                        {c.replace('_', ' ')}
                    </span>
                ))}
                {ticket.sla_breaches && <Chip tone="danger">SLA {ticket.sla_countdown}</Chip>}
            </div>

            <section className="detail-panel-section">
                <h3>Ticket workflow</h3>
                <div className="support-workflow-grid">
                    <label>
                        <span>Status</span>
                        <select
                            className="select"
                            value={ticket.status}
                            disabled={!canCompose || patchingTicket}
                            onChange={(event) =>
                                void handlePatchTicket({
                                    status: event.target.value as TicketStatus
                                })
                            }
                        >
                            <option value="open">Open</option>
                            <option value="in_progress">In progress</option>
                            <option value="waiting_customer">Waiting customer</option>
                            <option value="waiting_internal">Waiting internal</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                            <option value="reopened">Reopened</option>
                        </select>
                    </label>
                    <label>
                        <span>Priority</span>
                        <select
                            className="select"
                            value={ticket.priority}
                            disabled={!canCompose || patchingTicket}
                            onChange={(event) =>
                                void handlePatchTicket({
                                    priority: event.target.value as TicketPriority
                                })
                            }
                        >
                            <option value="urgent">Urgent</option>
                            <option value="high">High</option>
                            <option value="normal">Normal</option>
                            <option value="low">Low</option>
                        </select>
                    </label>
                </div>
            </section>

            {user.data && (
                <section className="detail-panel-section">
                    <h3>Customer context</h3>
                    <dl className="kv-list">
                        <dt>Plan</dt>
                        <dd>
                            <Chip tone="purple">{user.data.plan}</Chip>
                        </dd>
                        <dt>Signed up</dt>
                        <dd>{user.data.signed_up}</dd>
                        <dt>Last active</dt>
                        <dd>{user.data.last_active}</dd>
                        <dt>Lifetime revenue</dt>
                        <dd>${user.data.revenue_lifetime.toLocaleString()}</dd>
                        <dt>Tracks</dt>
                        <dd>{user.data.tracks.toLocaleString()}</dd>
                    </dl>
                </section>
            )}

            <section className="detail-panel-section" style={{ padding: 0 }}>
                <h3 style={{ padding: '12px 20px 0' }}>Conversation</h3>
                <div className="ticket-thread">
                    {transcript.map((m) => {
                        const internal = isInternalTicketMessage(m);
                        const label = getSupportTranscriptLabel(m);
                        return (
                            <article
                                key={m.id}
                                className="ticket-message"
                                data-author={
                                    m.author_type === 'assistant'
                                        ? 'glimbr_ai'
                                        : m.author_type === 'internal_note'
                                          ? 'admin'
                                          : m.author_type
                                }
                                data-internal={internal}
                            >
                                <header className="ticket-message-head">
                                    <span className="author">
                                        {m.author_name}
                                        <span className="text-muted"> · {label}</span>
                                    </span>
                                    <ChannelIcon channel={m.channel} />
                                    <span>{m.ts}</span>
                                    {internal && <Chip tone="warning">internal note</Chip>}
                                    {m.author_type === 'system' && (
                                        <Chip tone="muted">system event</Chip>
                                    )}
                                    {m.event_type && <Chip tone="muted">{m.event_type}</Chip>}
                                    {m.ai_confidence !== undefined && (
                                        <span className="text-muted">
                                            conf {(m.ai_confidence * 100).toFixed(0)}%
                                        </span>
                                    )}
                                </header>
                                <div style={{ whiteSpace: 'pre-wrap' }}>{m.body_text}</div>
                            </article>
                        );
                    })}
                    {transcript.length === 0 && (
                        <div className="empty-state" style={{ padding: 24 }}>
                            <div className="title">No messages yet</div>
                        </div>
                    )}
                </div>
            </section>

            <section className="detail-panel-section">
                <h3>Canned replies</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {canned.data?.map((r) => (
                        <button
                            key={r.slug}
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleInsertCanned(r.slug)}
                            style={{ justifyContent: 'flex-start', width: '100%' }}
                        >
                            {r.title}
                        </button>
                    ))}
                </div>
            </section>

            <section className="detail-panel-section">
                <h3>Composer</h3>
                {canCompose ? (
                    <div className="ticket-composer" data-internal={internalNote}>
                        <textarea
                            placeholder={
                                internalNote
                                    ? 'Internal note — visible only to admins'
                                    : `Reply via ${channel.replace('_', ' ')}…`
                            }
                            value={replyBody}
                            disabled={sending !== null}
                            onChange={(e) => setReplyBody(e.target.value)}
                        />
                        <div className="ticket-composer-toolbar">
                            <select
                                className="select"
                                value={channel}
                                onChange={(e) => setChannel(e.target.value as TicketChannel)}
                                disabled={internalNote || sending !== null}
                                aria-label="Channel"
                            >
                                {channelOptions.map((c) => (
                                    <option key={c} value={c}>
                                        {c.replace('_', ' ')}
                                    </option>
                                ))}
                            </select>
                            <label
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    fontSize: 11,
                                    color: 'var(--text-soft)',
                                    cursor: 'pointer'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={internalNote}
                                    disabled={sending !== null}
                                    onChange={(e) => setInternalNote(e.target.checked)}
                                />
                                Internal note
                            </label>
                            <button
                                className="btn btn-sm btn-ghost"
                                type="button"
                                title="AI-suggest reply (Glimbr)"
                                onClick={handleAiSuggest}
                                disabled={sending !== null}
                            >
                                <Sparkles size={11} /> AI-suggest
                            </button>
                            <span className="text-muted" style={{ fontSize: 11 }}>
                                Send marks waiting customer. Internal notes mark waiting internal.
                            </span>
                            <span className="spacer" />
                            <button
                                className="btn btn-sm btn-ghost"
                                type="button"
                                onClick={() => void handleSend('resolved')}
                                disabled={!replyBody.trim() || internalNote || sending !== null}
                            >
                                {sending === 'resolved' ? 'Resolving…' : 'Send & resolve'}
                            </button>
                            <button
                                className="btn btn-sm btn-primary"
                                type="button"
                                onClick={() =>
                                    void handleSend(
                                        internalNote ? 'waiting_internal' : 'waiting_customer'
                                    )
                                }
                                disabled={!replyBody.trim() || sending !== null}
                            >
                                <Send size={11} />{' '}
                                {sending ? 'Sending…' : internalNote ? 'Save note' : 'Send'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="empty-state" style={{ padding: 16 }}>
                        <div className="title">Read-only access</div>
                        <div>
                            Support replies and internal notes are limited to support and owner
                            roles.
                        </div>
                    </div>
                )}
            </section>
        </>
    );
}
